/**
 * MoreMenu - Popup menu for mobile navigation
 *
 * Simple: overlay click closes, menu click doesn't.
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useOwnerAccess } from '@/hooks/useOwnerAccess';
import { useLanguage } from '@/i18n';
import './MoreMenu.css';

interface MoreMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MoreMenu({ isOpen, onClose }: MoreMenuProps) {
  const navigate = useNavigate();
  const { isOwner } = useOwnerAccess();
  const { t } = useLanguage();

  // Lock body scroll when open
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [isOpen]);

  // Escape key closes
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleNavigate = (path: string) => {
    onClose();
    navigate(path);
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        padding: '0 16px 80px 16px',
        background: 'rgba(0, 0, 0, 0.6)',
      }}
      onClick={onClose} // Click overlay = close
    >
      <div
        style={{
          width: '220px',
          background: '#1a1b26',
          border: '1px solid #2a2b3d',
          borderRadius: '16px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()} // Click menu = don't close
      >
        <div className="popup-menu-header">{t('nav.menu')}</div>
        <div className="popup-menu-items">
          <button className="popup-menu-item" onClick={() => handleNavigate('/categories')}>
            <span className="popup-menu-icon">🏷️</span>
            <span className="popup-menu-label">{t('nav.categories')}</span>
          </button>
          <button className="popup-menu-item" onClick={() => handleNavigate('/import')}>
            <span className="popup-menu-icon">📥</span>
            <span className="popup-menu-label">{t('nav.import')}</span>
          </button>
          <button className="popup-menu-item" onClick={() => handleNavigate('/settings')}>
            <span className="popup-menu-icon">⚙️</span>
            <span className="popup-menu-label">{t('nav.settings')}</span>
          </button>
          {isOwner && (
            <>
              <div className="popup-menu-divider" />
              <button className="popup-menu-item popup-menu-item--dev" onClick={() => handleNavigate('/dev')}>
                <span className="popup-menu-icon">🛠️</span>
                <span className="popup-menu-label">Dev Tools</span>
              </button>
              <button className="popup-menu-item popup-menu-item--dev" onClick={() => handleNavigate('/metrics_dev')}>
                <span className="popup-menu-icon">M</span>
                <span className="popup-menu-label">Dev Metrics</span>
              </button>
              <button className="popup-menu-item popup-menu-item--dev" onClick={() => handleNavigate('/log')}>
                <span className="popup-menu-icon">📜</span>
                <span className="popup-menu-label">Logs</span>
              </button>
            </>
          )}
        </div>
        <button className="popup-menu-close" onClick={onClose}>{t('common.close')}</button>
      </div>
    </div>,
    document.body
  );
}
